import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  HeartIcon, 
  ShareIcon, 
  ChatBubbleLeftIcon,
  UserPlusIcon,
  ArrowLeftIcon,
  MusicalNoteIcon,
  ShoppingCartIcon,
  BookmarkIcon,
  StarIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useLanguage } from '../contexts/LanguageContext';

interface Song {
  id: number;
  title: string;
  duration: string;
  plays: number;
  likes: number;
  coverUrl?: string;
}

interface Work {
  id: number;
  title: string;
  lyricist: string;
  composer: string;
  singer: string;
  creatorRole: string[];
  tags: string[];
  rating: number;
  ratingCount: number;
  trialDuration: string;
  trialCount: number;
  price: number;
  currency: string;
  coverUrl: string;
  previewUrl?: string;
}

interface Demo {
  id: number;
  title: string;
  lyricist: string;
  composer: string;
  singer: string;
  creatorRole: string[];
  tags: string[];
  rating: number;
  ratingCount: number;
  trialDuration: string;
  trialCount: number;
  price: number;
  currency: string;
  coverUrl: string;
  participants: Array<{
    role: string;
    name: string;
  }>;
}

interface MusicianDetailData {
  id: number;
  name: string;
  avatar: string;
  coverImage: string;
  genre: string;
  location: string;
  followers: number;
  following: number;
  plays: number;
  rating: number;
  verified: boolean;
  description: string;
  tags: string[];
  songs: Song[];
  works: Work[];
  demos: Demo[];
  relatedMusicians: Array<{
    id: number;
    name: string;
    avatar: string;
    genre: string;
    followers: number;
  }>;
}

const MusicianDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [musician, setMusician] = useState<MusicianDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'works' | 'demos'>('works');
  const [isFollowing, setIsFollowing] = useState(false);
  const { t, language } = useLanguage();

  // Translation helper function with fallbacks
  const getTranslation = (key: string, fallback: string) => {
    try {
      const translation = t(key);
      return (translation === key || !translation) ? fallback : translation;
    } catch (error) {
      return fallback;
    }
  };

  useEffect(() => {
    const fetchMusicianDetail = async () => {
      try {
        // Mock data for demonstration with proper fallback translations
        const musicianData = {
          id: parseInt(id || '1'),
          name: getTranslation('musician.detail.name', language === 'zh' ? '张小明' : 'Zhang Xiaoming'),
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
          coverImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
          genre: getTranslation('musician.detail.genre', language === 'zh' ? '流行歌手' : 'Pop Singer'),
          location: getTranslation('musician.detail.location', language === 'zh' ? '北京, 中国' : 'Beijing, China'),
          followers: 2.8,
          following: 328,
          plays: 896,
          rating: 4.2,
          verified: true,
          description: getTranslation('musician.detail.description', language === 'zh' ? '一位才华横溢的音乐人，专注于创作动人的流行歌曲。拥有丰富的创作经验和独特的音乐风格。' : 'A talented musician focused on creating moving pop songs. Has rich creative experience and a unique musical style.'),
          tags: [
            getTranslation('musician.detail.tag.pop', language === 'zh' ? '流行' : 'Pop'),
            getTranslation('musician.detail.tag.rock', language === 'zh' ? '摇滚' : 'Rock'),
            getTranslation('musician.detail.tag.creative', language === 'zh' ? '创新' : 'Creative'),
            getTranslation('musician.detail.tag.vocal', language === 'zh' ? '人声' : 'Vocal')
          ],
          songs: [
            {
              id: 1,
              title: getTranslation('song.1.title', language === 'zh' ? '梦想之歌' : 'Song of Dreams'),
              duration: '04:31',
              plays: 24000,
              likes: 856,
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 2,
              title: getTranslation('song.2.title', language === 'zh' ? '夜色温柔' : 'Gentle Night'),
              duration: '04:18',
              plays: 18000,
              likes: 623,
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 3,
              title: getTranslation('song.3.title', language === 'zh' ? '青春记忆' : 'Youth Memories'),
              duration: '03:43',
              plays: 32000,
              likes: 1200,
              coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 4,
              title: getTranslation('song.4.title', language === 'zh' ? '星空下的故事' : 'Stories Under Stars'),
              duration: '04:45',
              plays: 15000,
              likes: 432,
              coverUrl: 'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 5,
              title: getTranslation('song.5.title', language === 'zh' ? '心的旋律' : 'Melody of Heart'),
              duration: '03:53',
              plays: 28000,
              likes: 987,
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 6,
              title: getTranslation('song.6.title', language === 'zh' ? '城市光影' : 'City Lights'),
              duration: '04:19',
              plays: 45000,
              likes: 1500,
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 7,
              title: getTranslation('song.7.title', language === 'zh' ? '风中的呢喃' : 'Whispers in Wind'),
              duration: '03:25',
              plays: 22000,
              likes: 678,
              coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 8,
              title: getTranslation('song.8.title', language === 'zh' ? '时光倒流' : 'Time Rewind'),
              duration: '04:45',
              plays: 19000,
              likes: 534,
              coverUrl: 'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 9,
              title: getTranslation('song.9.title', language === 'zh' ? '彩虹的约定' : 'Promise of Rainbow'),
              duration: '03:52',
              plays: 35000,
              likes: 1100,
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            },
            {
              id: 10,
              title: getTranslation('song.10.title', language === 'zh' ? '远方的呼唤' : 'Call from Distance'),
              duration: '04:10',
              plays: 41000,
              likes: 1350,
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
            }
          ],
          works: [
            {
              id: 1,
              title: language === 'zh' ? '为梦而飞' : 'Dreams in Flight',
              lyricist: language === 'zh' ? '张丹' : 'Zhang Dan',
              composer: language === 'zh' ? '张丹' : 'Zhang Dan',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['演唱', '编曲'] : ['Singer', 'Arranger'],
              tags: language === 'zh' ? ['流行', '创新', '有人声', '90BPM', '4/4', '安静'] : ['Pop', 'Creative', 'Vocal', '90BPM', '4/4', 'Serene'],
              rating: 9.1,
              ratingCount: 856,
              trialDuration: '2:40',
              trialCount: 856,
              price: 500,
              currency: 'HK$',
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              previewUrl: '/audio/preview1.mp3'
            },
            {
              id: 2,
              title: language === 'zh' ? '朝阳暮岁都是你' : 'Morning Sunshine',
              lyricist: language === 'zh' ? '秦立南' : 'Qin Linan',
              composer: language === 'zh' ? '秦立南' : 'Qin Linan',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['演唱', '制作'] : ['Singer', 'Producer'],
              tags: language === 'zh' ? ['流行', '抒情', '有人声', '68BPM', '4/4', '抒情'] : ['Pop', 'Lyrical', 'Vocal', '68BPM', '4/4', 'Emotional'],
              rating: 8.7,
              ratingCount: 1024,
              trialDuration: '2:24',
              trialCount: 1024,
              price: 0,
              currency: 'HK$',
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              previewUrl: '/audio/preview2.mp3'
            },
            {
              id: 3,
              title: language === 'zh' ? '午夜蓝调' : 'Midnight Blues',
              lyricist: language === 'zh' ? '陈阿力' : 'Alex Chen',
              composer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['作曲', '演唱'] : ['Composer', 'Singer'],
              tags: language === 'zh' ? ['蓝调', '爵士', '吉他', '85BPM', '4/4', '忧郁'] : ['Blues', 'Jazz', 'Guitar', '85BPM', '4/4', 'Moody'],
              rating: 8.9,
              ratingCount: 642,
              trialDuration: '3:00',
              trialCount: 1200,
              price: 350,
              currency: 'HK$',
              coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              previewUrl: '/audio/preview3.mp3'
            },
            {
              id: 4,
              title: language === 'zh' ? '电子梦境' : 'Electric Dreams',
              lyricist: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              composer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              singer: language === 'zh' ? '刘萨拉' : 'Sara Liu',
              creatorRole: language === 'zh' ? ['作词', '作曲', '制作'] : ['Lyricist', 'Composer', 'Producer'],
              tags: language === 'zh' ? ['电子', '合成器', '舞曲', '128BPM', '4/4', '充满活力'] : ['Electronic', 'Synth', 'Dance', '128BPM', '4/4', 'Energetic'],
              rating: 9.5,
              ratingCount: 1024,
              trialDuration: '4:50',
              trialCount: 2100,
              price: 800,
              currency: 'HK$',
              coverUrl: 'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              previewUrl: '/audio/preview4.mp3'
            },
            {
              id: 5,
              title: language === 'zh' ? '原声之心' : 'Acoustic Heart',
              lyricist: language === 'zh' ? '黄大卫' : 'David Wong',
              composer: language === 'zh' ? '黄大卫' : 'David Wong',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['演唱', '吉他'] : ['Singer', 'Guitar'],
              tags: language === 'zh' ? ['民谣', '原声', '吉他', '75BPM', '3/4', '温柔'] : ['Folk', 'Acoustic', 'Guitar', '75BPM', '3/4', 'Gentle'],
              rating: 7.8,
              ratingCount: 445,
              trialDuration: '2:80',
              trialCount: 890,
              price: 250,
              currency: 'HK$',
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              previewUrl: '/audio/preview5.mp3'
            }
          ],
          demos: [
            {
              id: 1,
              title: language === 'zh' ? '未命名演示 1' : 'Untitled Demo 1',
              lyricist: language === 'zh' ? '张丹' : 'Zhang Dan',
              composer: language === 'zh' ? '李伟' : 'Li Wei',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['演唱'] : ['Singer'],
              tags: language === 'zh' ? ['演示', '流行', '人声'] : ['Demo', 'Pop', 'Vocal'],
              rating: 7.5,
              ratingCount: 324,
              trialDuration: '1:50',
              trialCount: 456,
              price: 0,
              currency: '',
              coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              participants: [
                { role: language === 'zh' ? '主唱' : 'Vocalist', name: language === 'zh' ? '张小明' : 'Zhang Xiaoming' },
                { role: language === 'zh' ? '制作人' : 'Producer', name: language === 'zh' ? '李伟' : 'Li Wei' }
              ]
            },
            {
              id: 2,
              title: language === 'zh' ? '实验音轨' : 'Experimental Track',
              lyricist: language === 'zh' ? '王明' : 'Wang Ming',
              composer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['作曲', '演唱'] : ['Composer', 'Singer'],
              tags: language === 'zh' ? ['演示', '摇滚', '吉他'] : ['Demo', 'Rock', 'Guitar'],
              rating: 8.2,
              ratingCount: 567,
              trialDuration: '2:00',
              trialCount: 789,
              price: 0,
              currency: '',
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              participants: [
                { role: language === 'zh' ? '作曲/演唱' : 'Composer/Singer', name: language === 'zh' ? '张小明' : 'Zhang Xiaoming' },
                { role: language === 'zh' ? '吉他手' : 'Guitarist', name: language === 'zh' ? '王明' : 'Wang Ming' },
                { role: language === 'zh' ? '鼓手' : 'Drummer', name: language === 'zh' ? '陈宇' : 'Chen Yu' }
              ]
            },
            {
              id: 3,
              title: language === 'zh' ? '爵士融合演示' : 'Jazz Fusion Demo',
              lyricist: language === 'zh' ? '陈萨拉' : 'Sarah Chen',
              composer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['作曲', '演唱', '钢琴'] : ['Composer', 'Singer', 'Piano'],
              tags: language === 'zh' ? ['演示', '爵士', '融合'] : ['Demo', 'Jazz', 'Fusion'],
              rating: 9.1,
              ratingCount: 234,
              trialDuration: '1:80',
              trialCount: 345,
              price: 0,
              currency: '',
              coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              participants: [
                { role: language === 'zh' ? '作曲/演唱/钢琴' : 'Composer/Singer/Piano', name: language === 'zh' ? '张小明' : 'Zhang Xiaoming' },
                { role: language === 'zh' ? '萨克斯风' : 'Saxophone', name: language === 'zh' ? '陈萨拉' : 'Sarah Chen' }
              ]
            },
            {
              id: 4,
              title: language === 'zh' ? '环境音乐草图' : 'Ambient Sketch',
              lyricist: language === 'zh' ? '黄亚历克斯' : 'Alex Wong',
              composer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              singer: language === 'zh' ? '张小明' : 'Zhang Xiaoming',
              creatorRole: language === 'zh' ? ['作曲', '制作'] : ['Composer', 'Producer'],
              tags: language === 'zh' ? ['演示', '环境音乐', '电子'] : ['Demo', 'Ambient', 'Electronic'],
              rating: 6.8,
              ratingCount: 123,
              trialDuration: '2:50',
              trialCount: 234,
              price: 0,
              currency: '',
              coverUrl: 'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
              participants: [
                { role: language === 'zh' ? '作曲/制作' : 'Composer/Producer', name: language === 'zh' ? '张小明' : 'Zhang Xiaoming' },
                { role: language === 'zh' ? '音效设计师' : 'Sound Designer', name: language === 'zh' ? '杨丽莎' : 'Lisa Yang' }
              ]
            }
          ],
          relatedMusicians: [
            {
              id: 2,
              name: getTranslation('musician.detail.related.musician.1', language === 'zh' ? '王小红' : 'Wang Xiaohong'),
              avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              genre: getTranslation('musician.detail.genre', language === 'zh' ? '流行歌手' : 'Pop Singer'),
              followers: 15400
            },
            {
              id: 3,
              name: getTranslation('musician.detail.related.musician.2', language === 'zh' ? '李大伟' : 'Li Dawei'),
              avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              genre: getTranslation('musician.detail.genre', language === 'zh' ? '流行歌手' : 'Pop Singer'),
              followers: 25600
            },
            {
              id: 4,
              name: getTranslation('musician.detail.related.musician.3', language === 'zh' ? '陈美丽' : 'Chen Meili'),
              avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              genre: getTranslation('musician.detail.genre', language === 'zh' ? '流行歌手' : 'Pop Singer'),
              followers: 32100
            }
          ]
        };
        
        // Ensure all arrays exist before setting the musician
        if (musicianData.songs && musicianData.works && musicianData.demos && musicianData.relatedMusicians) {
          setMusician(musicianData);
        } else {
          console.error('Missing required arrays in musician data');
          setMusician(null);
        }
      } catch (error) {
        console.error('Failed to fetch musician detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMusicianDetail();
  }, [id, language]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: musician?.name || '',
          text: getTranslation('musician.detail.about.artist', language === 'zh' ? '关于音乐人' : 'About Artist'),
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert(getTranslation('musician.detail.share', language === 'zh' ? '链接已复制！' : 'Link copied!'));
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!musician) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {getTranslation('musician.detail.not.found', language === 'zh' ? '未找到音乐人' : 'Musician not found')}
          </h2>
          <button
            onClick={() => navigate('/musicians')}
            className="text-blue-600 hover:text-blue-800"
          >
            {getTranslation('musician.detail.back.to.list', language === 'zh' ? '返回列表' : 'Back to list')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-[500px] bg-gradient-to-b from-transparent to-black">
        <img 
          src={musician.coverImage} 
          alt={musician.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80"></div>
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/musicians')}
          className="absolute top-8 left-8 bg-black bg-opacity-50 backdrop-blur-sm text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>

        {/* Musician Info */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end space-x-6">
              <img 
                src={musician.avatar}
                alt={musician.name}
                className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
              />
              <div className="flex-1 text-white">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-5xl font-bold">{musician.name}</h1>
                  {musician.verified && (
                    <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                      <CheckBadgeIcon className="h-4 w-4" />
                      <span>{getTranslation('musician.detail.verified', language === 'zh' ? '认证' : 'Verified')}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1 bg-black bg-opacity-50 backdrop-blur-sm rounded-full px-3 py-1">
                    <StarIconSolid className="h-5 w-5 text-yellow-400" />
                    <span className="text-white font-medium">{musician.rating}</span>
                  </div>
                </div>
                <p className="text-xl text-gray-200 mb-4">{musician.location}</p>
                <div className="flex items-center space-x-8 text-sm">
                  <div>
                    <span className="text-2xl font-bold">{musician.followers}M</span>
                    <span className="text-gray-300 ml-1">{getTranslation('musician.detail.followers', language === 'zh' ? '粉丝' : 'followers')}</span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold">{musician.following}</span>
                    <span className="text-gray-300 ml-1">{getTranslation('musician.detail.following', language === 'zh' ? '关注' : 'following')}</span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold">{musician.plays}</span>
                    <span className="text-gray-300 ml-1">{getTranslation('musician.detail.works', language === 'zh' ? '作品' : 'works')}</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button 
                  onClick={() => setIsFollowing(!isFollowing)}
                  className={`flex items-center space-x-2 px-8 py-3 rounded-full font-medium transition-all ${
                    isFollowing 
                      ? 'bg-gray-600 text-white hover:bg-gray-700' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <UserPlusIcon className="h-5 w-5" />
                  <span>
                    {isFollowing 
                      ? getTranslation('musician.detail.followed', language === 'zh' ? '已关注' : 'Following')
                      : getTranslation('musician.detail.follow', language === 'zh' ? '关注' : 'Follow')
                    }
                  </span>
                </button>
                <button 
                  className="flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm text-white px-6 py-3 rounded-full hover:bg-opacity-30 transition-all"
                  onClick={() => navigate('/chat')}
                >
                  <ChatBubbleLeftIcon className="h-5 w-5" />
                  <span>{getTranslation('musician.detail.message', language === 'zh' ? '私信' : 'Message')}</span>
                </button>
                <button 
                  className="flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm text-white px-6 py-3 rounded-full hover:bg-opacity-30 transition-all"
                  onClick={handleShare}
                >
                  <ShareIcon className="h-5 w-5" />
                  <span>{getTranslation('musician.detail.share', language === 'zh' ? '分享' : 'Share')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <div className="mb-8">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { key: 'works', label: language === 'zh' ? '作品' : 'Works', icon: MusicalNoteIcon },
                    { key: 'demos', label: language === 'zh' ? '演示' : 'Demos', icon: PlayIcon }
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key as any)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === key
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'works' && (
              <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100">
                {/* Table Header */}
                <div className="bg-gray-50 px-6 py-4">
                  <div className="grid grid-cols-11 gap-4 text-sm font-medium text-gray-700">
                    <div className="col-span-3">{language === 'zh' ? '歌曲作品' : 'Songs'}</div>
                    <div className="col-span-2">{language === 'zh' ? '标签' : 'Tags'}</div>
                    <div className="col-span-1">{language === 'zh' ? '评分' : 'Rating'}</div>
                    <div className="col-span-1">{language === 'zh' ? '试唱' : 'Audition'}</div>
                    <div className="col-span-1">{language === 'zh' ? '意向价格' : 'Price'}</div>
                    <div className="col-span-3">{language === 'zh' ? '操作' : 'Actions'}</div>
                  </div>
                </div>
                
                {/* Works List */}
                <div className="divide-y divide-gray-200">
                  {musician.works && musician.works.length > 0 ? musician.works.map((work) => (
                    <div key={work.id} className="px-6 py-6 hover:bg-gray-50 transition-colors border-b border-gray-100">
                      <div className="grid grid-cols-11 gap-4 items-center">
                        {/* Song Info */}
                        <div className="col-span-3 flex items-start space-x-4">
                          <div className="relative flex-shrink-0">
                            <img 
                              src={work.coverUrl}
                              alt={work.title}
                              className="w-20 h-20 rounded-lg object-cover shadow-md"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAyNkg0MFYzOEgyNFYyNloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                              }}
                            />
                            <button 
                              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 hover:opacity-100 transition-opacity"
                              onClick={() => alert(`Playing ${work.title}`)}
                            >
                              <PlayIcon className="h-6 w-6 text-white" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-0 space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-lg mb-1 hover:text-blue-600 cursor-pointer transition-colors">{work.title}</h4>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">{language === 'zh' ? '词：' : 'Lyrics: '}{work.lyricist}</p>
                                <p className="text-sm text-gray-600">{language === 'zh' ? '曲：' : 'Music: '}{work.composer}</p>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <button 
                                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors font-medium"
                                onClick={() => alert(language === 'zh' ? `播放完整版 ${work.title}` : `Playing full version of ${work.title}`)}
                              >
                                {language === 'zh' ? '完整版' : 'Full'}
                              </button>
                              <button 
                                className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors font-medium"
                                onClick={() => alert(language === 'zh' ? `播放副歌 ${work.title}` : `Playing chorus of ${work.title}`)}
                              >
                                {language === 'zh' ? '副歌' : 'Chorus'}
                              </button>
                              <button 
                                className="px-3 py-1.5 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors font-medium"
                                onClick={() => alert(language === 'zh' ? `查看歌词 ${work.title}` : `Showing lyrics of ${work.title}`)}
                              >
                                {language === 'zh' ? '歌词' : 'Lyrics'}
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tags */}
                        <div className="col-span-2">
                          <div className="space-y-2">
                            {/* Creator Role */}
                            <div className="bg-blue-50 px-3 py-2 rounded-lg">
                              <div className="text-xs font-medium text-blue-800 mb-1">{language === 'zh' ? '担任' : 'Role'}</div>
                              <div className="flex flex-wrap gap-1">
                                {work.creatorRole.map((role, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                                    {role}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* Song Tags */}
                            <div className="flex flex-wrap gap-1">
                              {work.tags.slice(0, 3).map((tag, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Rating */}
                        <div className="col-span-1">
                          <div className="flex items-center space-x-1 mb-1">
                            <StarIcon className="h-4 w-4 text-yellow-400" />
                            <span className="text-sm font-medium">{work.rating}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                            <HeartIcon className="h-3 w-3" />
                            <span>{work.ratingCount}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <PlayIcon className="h-3 w-3" />
                            <span>{work.trialDuration}</span>
                          </div>
                        </div>
                        
                        {/* Audition */}
                        <div className="col-span-1">
                          <button 
                            className="px-3 py-1 bg-orange-100 text-orange-600 text-xs rounded-full hover:bg-orange-200 transition-colors"
                            onClick={() => navigate(`/karaoke/${work.id}`)}
                          >
                            {language === 'zh' ? '试唱' : 'Tryout'}
                          </button>
                        </div>
                        
                        {/* Price */}
                        <div className="col-span-1">
                          {work.price === 0 ? (
                            <span className="text-sm text-green-600 font-medium">{language === 'zh' ? '可协商' : 'Negotiable'}</span>
                          ) : (
                            <span className="text-sm font-medium">{work.currency} {work.price}</span>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-3 flex space-x-2">
                          <button 
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full hover:bg-blue-700 transition-colors flex items-center space-x-1"
                            onClick={() => alert(language === 'zh' ? `购买 ${work.title} 价格 ${work.currency}${work.price}` : `Buying ${work.title} for ${work.currency}${work.price}`)}
                          >
                            <ShoppingCartIcon className="h-3 w-3" />
                            <span>{language === 'zh' ? '购买' : 'Buy'}</span>
                          </button>
                          <button 
                            className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 transition-colors flex items-center space-x-1"
                            onClick={() => alert(language === 'zh' ? `收藏了 ${work.title}` : `Collected ${work.title}`)}
                          >
                            <BookmarkIcon className="h-3 w-3" />
                            <span>{language === 'zh' ? '收藏' : 'Collect'}</span>
                          </button>
                          <button 
                            className="px-3 py-1 bg-red-100 text-red-600 text-sm rounded-full hover:bg-red-200 transition-colors flex items-center space-x-1"
                            onClick={() => alert(language === 'zh' ? `点赞了 ${work.title}` : `Liked ${work.title}`)}
                          >
                            <HeartIcon className="h-3 w-3" />
                            <span>{language === 'zh' ? '点赞' : 'Like'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                      {language === 'zh' ? '暂无作品' : 'No works available'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'demos' && (
              <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100">
                {/* Table Header */}
                <div className="bg-gray-50 px-6 py-4">
                  <div className="grid grid-cols-11 gap-4 text-sm font-medium text-gray-700">
                    <div className="col-span-3">{language === 'zh' ? '演示作品' : 'Demo Songs'}</div>
                    <div className="col-span-2">{language === 'zh' ? '标签' : 'Tags'}</div>
                    <div className="col-span-1">{language === 'zh' ? '评分' : 'Rating'}</div>
                    <div className="col-span-1">{language === 'zh' ? '试唱' : 'Audition'}</div>
                    <div className="col-span-1">{language === 'zh' ? '状态' : 'Status'}</div>
                    <div className="col-span-3">{language === 'zh' ? '操作' : 'Actions'}</div>
                  </div>
                </div>
                
                {/* Demos List */}
                <div className="divide-y divide-gray-200">
                  {musician.demos && musician.demos.length > 0 ? musician.demos.map((demo) => (
                    <div key={demo.id} className="px-6 py-6 hover:bg-gray-50 transition-colors border-b border-gray-100">
                      <div className="grid grid-cols-11 gap-4 items-center">
                        {/* Song Info */}
                        <div className="col-span-3 flex items-start space-x-4">
                          <div className="relative flex-shrink-0">
                            <img 
                              src={demo.coverUrl}
                              alt={demo.title}
                              className="w-20 h-20 rounded-lg object-cover shadow-md"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAyNkg0MFYzOEgyNFYyNloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                              }}
                            />
                            <button 
                              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 hover:opacity-100 transition-opacity"
                              onClick={() => alert(`Playing demo ${demo.title}`)}
                            >
                              <PlayIcon className="h-6 w-6 text-white" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-0 space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-lg mb-1 hover:text-blue-600 cursor-pointer transition-colors">{demo.title}</h4>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">{language === 'zh' ? '词：' : 'Lyrics: '}{demo.lyricist}</p>
                                <p className="text-sm text-gray-600">{language === 'zh' ? '曲：' : 'Music: '}{demo.composer}</p>
                              </div>
                            </div>
                            
                            <div className="bg-blue-50 px-3 py-2 rounded-lg">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-blue-800">{language === 'zh' ? '担任' : 'Role'}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {demo.creatorRole.map((role, index) => (
                                  <span key={index} className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                                    {role}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            <div className="bg-orange-50 px-3 py-1.5 rounded-lg inline-block">
                              <span className="text-xs font-medium text-orange-700">{language === 'zh' ? 'Demo版本' : 'Demo Version'}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tags */}
                        <div className="col-span-2">
                          <div className="space-y-2">
                            {/* Creator Role */}
                            <div className="bg-blue-50 px-3 py-2 rounded-lg">
                              <div className="text-xs font-medium text-blue-800 mb-1">{language === 'zh' ? '担任' : 'Role'}</div>
                              <div className="flex flex-wrap gap-1">
                                {demo.creatorRole.map((role, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                                    {role}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* Song Tags */}
                            <div className="flex flex-wrap gap-1">
                              {demo.tags.slice(0, 3).map((tag, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Rating */}
                        <div className="col-span-1">
                          <div className="flex items-center space-x-1 mb-1">
                            <StarIcon className="h-4 w-4 text-yellow-400" />
                            <span className="text-sm font-medium">{demo.rating}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                            <HeartIcon className="h-3 w-3" />
                            <span>{demo.ratingCount}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <PlayIcon className="h-3 w-3" />
                            <span>{demo.trialDuration}</span>
                          </div>
                        </div>
                        
                        {/* Audition */}
                        <div className="col-span-1">
                          <button 
                            className="px-3 py-1 bg-orange-100 text-orange-600 text-xs rounded-full hover:bg-orange-200 transition-colors"
                            onClick={() => navigate(`/karaoke/${demo.id}`)}
                          >
                            {language === 'zh' ? '试唱' : 'Tryout'}
                          </button>
                        </div>
                        
                        {/* Status */}
                        <div className="col-span-1">
                          <span className="text-sm text-green-600 font-medium">{language === 'zh' ? 'Demo' : 'Demo'}</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-3 flex space-x-2">
                          <button 
                            className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 transition-colors flex items-center space-x-1"
                            onClick={() => alert(language === 'zh' ? `收藏了演示 ${demo.title}` : `Collected demo ${demo.title}`)}
                          >
                            <BookmarkIcon className="h-3 w-3" />
                            <span>{language === 'zh' ? '收藏' : 'Collect'}</span>
                          </button>
                          <button 
                            className="px-3 py-1 bg-red-100 text-red-600 text-sm rounded-full hover:bg-red-200 transition-colors flex items-center space-x-1"
                            onClick={() => alert(language === 'zh' ? `点赞了演示 ${demo.title}` : `Liked demo ${demo.title}`)}
                          >
                            <HeartIcon className="h-3 w-3" />
                            <span>{language === 'zh' ? '点赞' : 'Like'}</span>
                          </button>
                          <button 
                            className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-full hover:bg-blue-200 transition-colors"
                            onClick={() => alert(language === 'zh' ? `联系音乐人关于 ${demo.title}` : `Contacting artist about ${demo.title}`)}
                          >
                            {language === 'zh' ? '联系' : 'Contact'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                      {language === 'zh' ? '暂无演示作品' : 'No demos available'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* About */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {getTranslation('musician.detail.about.artist', language === 'zh' ? '关于音乐人' : 'About Artist')}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">{musician.description}</p>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {musician.tags && musician.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-blue-50 text-blue-600 text-sm rounded-full font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Recent Works */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {getTranslation('musician.detail.recent.works', language === 'zh' ? '最新作品' : 'Recent Works')}
              </h3>
              <div className="space-y-4">
                {musician.songs && musician.songs.length > 0 ? musician.songs.slice(0, 3).map((song) => (
                  <div key={song.id} className="flex items-center space-x-3">
                    <img 
                      src={song.coverUrl}
                      alt={song.title}
                      className="w-12 h-12 rounded object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xOCAxOEgzMFYzMEgxOFYxOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{song.title}</h4>
                      <p className="text-xs text-gray-500">
                        {(song.plays / 1000).toFixed(1)}k {getTranslation('musician.detail.plays', language === 'zh' ? '播放' : 'plays')}
                      </p>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <PlayIcon className="h-4 w-4" />
                    </button>
                  </div>
                )) : (
                  <div className="text-center text-gray-500 text-sm">
                    {language === 'zh' ? '暂无歌曲' : 'No songs available'}
                  </div>
                )}
              </div>
            </div>

            {/* Recommended Musicians */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {getTranslation('musician.detail.you.might.like', language === 'zh' ? '您可能喜欢' : 'You Might Like')}
              </h3>
              <div className="space-y-4">
                {musician.relatedMusicians && musician.relatedMusicians.length > 0 ? musician.relatedMusicians.slice(0, 3).map((relatedMusician) => (
                  <div key={relatedMusician.id} className="flex items-center space-x-3">
                    <img 
                      src={relatedMusician.avatar}
                      alt={relatedMusician.name}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNGM0Y0RjYiLz4KPHBhdGggZD0iTTE4IDI0QzE4IDIwLjY4NjMgMjAuNjg2MyAxOCAyNCAxOEMyNy4zMTM3IDE4IDMwIDIwLjY4NjMgMzAgMjRDMzAgMjcuMzEzNyAyNy4zMTM3IDMwIDI0IDMwQzIwLjY4NjMgMzAgMTggMjcuMzEzNyAxOCAyNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">{relatedMusician.name}</h4>
                      <p className="text-xs text-gray-500">
                        {(relatedMusician.followers / 1000).toFixed(1)}k {getTranslation('musician.detail.followers', language === 'zh' ? '粉丝' : 'followers')}
                      </p>
                    </div>
                    <button 
                      className="text-blue-600 text-sm hover:text-blue-800 transition-colors"
                      onClick={() => alert(language === 'zh' ? `关注了 ${relatedMusician.name}` : `Following ${relatedMusician.name}`)}
                    >
                      {getTranslation('musicians.follow', language === 'zh' ? '关注音乐人' : 'Follow')}
                    </button>
                  </div>
                )) : (
                  <div className="text-center text-gray-500 text-sm">
                    {language === 'zh' ? '暂无推荐音乐人' : 'No related musicians'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicianDetail;